import { useEffect, useRef } from 'react';
import debounce from 'lodash/debounce';

const getPointer = (touch) => ({ x: touch.clientX, y: touch.clientY });

const getDistance = (p1, p2) => {
  const powX = Math.pow(p1.x - p2.x, 2);
  const powY = Math.pow(p1.y - p2.y, 2);

  return Math.sqrt(powX + powY);
};

const getAngleDeg = (p1, p2) => {
  return (Math.atan2(p1.y - p2.y, p1.x - p2.x) * 180) / Math.PI;
};

const getGesture = (touches, minDelta) => {
  if (Math.abs(touches.offsetX) >= minDelta && Math.abs(touches.offsetY) < minDelta) {
    return touches.offsetX < 0 ? 'onSwipeLeft' : 'onSwipeRight';
  } else if (Math.abs(touches.offsetX) < minDelta && Math.abs(touches.offsetY) >= minDelta) {
    return touches.offsetY < 0 ? 'onSwipeUp' : 'onSwipeDown';
  }
};

export const useDraggable = (ref, handlers, options = { minDelta: 50 }) => {
  const prevTouch = useRef(null);
  const initialTouch = useRef(null);
  const gesture = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const getCurrentTouches = (originalEvent, touches, prevTouch) => {
      if (!touches) return {};

      const firstTouch = initialTouch.current;
      const pointer = getPointer(touches[0]);

      return {
        preventDefault: originalEvent.preventDefault,
        stopPropagation: originalEvent.stopPropagation,
        ...pointer,
        deltaX: prevTouch ? pointer.x - prevTouch.x : 0,
        deltaY: prevTouch ? pointer.y - prevTouch.y : 0,
        offsetX: firstTouch ? pointer.x - firstTouch.x : 0,
        offsetY: firstTouch ? pointer.y - firstTouch.y : 0,
        delta: prevTouch ? getDistance(pointer, prevTouch) : 0,
        distance: firstTouch ? getDistance(pointer, firstTouch) : 0,
        angleDeg: prevTouch ? getAngleDeg(pointer, prevTouch) : 0,
      };
    };

    const callHandler = (eventName, event) => handlers?.[eventName]?.(event);

    const handleTouchStart = (event) => {
      const currentTouches = getCurrentTouches(event, event.touches, null);
      prevTouch.current = currentTouches;
      initialTouch.current = currentTouches;

      callHandler('onPanStart', currentTouches);
    };

    const handleTouchMove = (event) => {
      const currentTouches = getCurrentTouches(event, event.touches, prevTouch.current);
      prevTouch.current = currentTouches;

      callHandler('onPanMove', currentTouches);

      gesture.current = getGesture(currentTouches, options.minDelta);

      if (gesture.current) {
        debounce(() => callHandler(gesture.current, currentTouches), 100)();
      }
    };

    const handleTouchEnd = (event) => {
      const currentTouches = getCurrentTouches(event, event.changedTouches, null);

      callHandler('onPanEnd', currentTouches);

      if (gesture.current) {
        callHandler(`${gesture.current}End`, currentTouches);
        gesture.current = undefined;
      }
    };

    element.addEventListener('touchstart', handleTouchStart);
    element.addEventListener('touchmove', handleTouchMove);
    element.addEventListener('touchend', handleTouchEnd);
    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
    };
  });
};
